
import React from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { Link } from 'react-router-dom';
import { useNavigationUtils } from '@/hooks/useNavigationUtils';
import { Button } from '../ui/button';
import { CheckCircle, Clock, DollarSign, BarChart4, ArrowRight } from 'lucide-react';

const ComparisonContent = () => {
  const { currentLanguage } = useLanguage();
  const { getServicePath } = useNavigationUtils();
  
  // Translations for comparison content
  const translations = {
    title: {
      de: "KI-Videomarketing vs. traditionelle Videoproduktion",
      en: "AI Video Marketing vs. Traditional Video Production",
      fr: "Marketing Vidéo IA vs. Production Vidéo Traditionnelle",
      it: "Marketing Video IA vs. Produzione Video Tradizionale"
    },
    subtitle: {
      de: "Ein umfassender Vergleich für Schweizer Unternehmen",
      en: "A comprehensive comparison for Swiss businesses",
      fr: "Une comparaison complète pour les entreprises suisses",
      it: "Un confronto completo per le aziende svizzere"
    },
    intro: {
      de: "Die Videoproduktion hat sich in den letzten Jahren stark verändert. Mit dem Aufkommen von KI-gestützten Tools können Unternehmen jetzt wählen zwischen traditionellen Produktionsmethoden und innovativen KI-Lösungen. Dieser Vergleich hilft Ihnen, die richtige Entscheidung für Ihr Unternehmen zu treffen.",
      en: "Video production has changed dramatically in recent years. With the emergence of AI-powered tools, businesses can now choose between traditional production methods and innovative AI solutions. This comparison will help you make the right decision for your business.",
      fr: "La production vidéo a considérablement évolué ces dernières années. Avec l'émergence d'outils alimentés par l'IA, les entreprises peuvent désormais choisir entre des méthodes de production traditionnelles et des solutions innovantes basées sur l'IA. Cette comparaison vous aidera à prendre la bonne décision pour votre entreprise.",
      it: "La produzione video è cambiata drasticamente negli ultimi anni. Con l'emergere di strumenti basati sull'IA, le aziende possono ora scegliere tra metodi di produzione tradizionali e soluzioni innovative di IA. Questo confronto ti aiuterà a prendere la decisione giusta per la tua azienda."
    },
    costHeading: {
      de: "Kosten",
      en: "Cost",
      fr: "Coût",
      it: "Costo"
    },
    costAI: {
      de: "KI-Videoproduktion ist deutlich kostengünstiger als traditionelle Methoden, mit Einsparungen von bis zu 80%. Keine Kosten für Equipment, Studio, Personal oder Nachbearbeitung.",
      en: "AI video production is significantly more cost-effective than traditional methods, with savings of up to 80%. No costs for equipment, studio, personnel or post-production.",
      fr: "La production vidéo IA est beaucoup plus rentable que les méthodes traditionnelles, avec des économies allant jusqu'à 80%. Pas de coûts pour l'équipement, le studio, le personnel ou la post-production.",
      it: "La produzione video IA è significativamente più conveniente rispetto ai metodi tradizionali, con risparmi fino all'80%. Nessun costo per attrezzature, studio, personale o post-produzione."
    },
    costTraditional: {
      de: "Hohe Kosten für Equipment, Studio, Crew, Schauspieler, Reise, Catering und Nachbearbeitung. Zusätzliche Kosten für Änderungen oder neue Versionen.",
      en: "High costs for equipment, studio, crew, actors, travel, catering, and post-production. Additional costs for changes or new versions.",
      fr: "Coûts élevés pour l'équipement, le studio, l'équipe, les acteurs, les voyages, la restauration et la post-production. Coûts supplémentaires pour les modifications ou les nouvelles versions.",
      it: "Costi elevati per attrezzature, studio, troupe, attori, viaggi, catering e post-produzione. Costi aggiuntivi per modifiche o nuove versioni."
    },
    timeHeading: {
      de: "Zeitaufwand",
      en: "Time",
      fr: "Temps",
      it: "Tempo"
    },
    timeAI: {
      de: "Schnelle Produktion in Stunden oder Tagen statt Wochen. Änderungen können in Minuten umgesetzt werden. Skalierbare Produktion für mehrere Videos gleichzeitig.",
      en: "Rapid production in hours or days instead of weeks. Changes can be implemented in minutes. Scalable production for multiple videos simultaneously.",
      fr: "Production rapide en heures ou en jours au lieu de semaines. Les modifications peuvent être mises en œuvre en quelques minutes. Production évolutive pour plusieurs vidéos simultanément.",
      it: "Produzione rapida in ore o giorni invece di settimane. Le modifiche possono essere implementate in pochi minuti. Produzione scalabile per più video contemporaneamente."
    },
    timeTraditional: {
      de: "Wochen oder Monate für Planung, Produktion und Nachbearbeitung. Komplexe Logistik und Koordination. Änderungen erfordern oft neue Drehtage.",
      en: "Weeks or months for planning, production, and post-production. Complex logistics and coordination. Changes often require new shooting days.",
      fr: "Des semaines ou des mois pour la planification, la production et la post-production. Logistique et coordination complexes. Les modifications nécessitent souvent de nouveaux jours de tournage.",
      it: "Settimane o mesi per pianificazione, produzione e post-produzione. Logistica e coordinamento complessi. Le modifiche spesso richiedono nuovi giorni di riprese."
    },
    qualityHeading: {
      de: "Qualität",
      en: "Quality",
      fr: "Qualité",
      it: "Qualità"
    },
    qualityAI: {
      de: "Konstant hohe Qualität mit modernen KI-Tools. Professionelles Erscheinungsbild mit anpassbaren Stilen. Perfekt für Produktvideos, Erklärvideos und Social Media.",
      en: "Consistently high quality with modern AI tools. Professional appearance with customizable styles. Perfect for product videos, explainer videos and social media.",
      fr: "Qualité constamment élevée avec des outils d'IA modernes. Apparence professionnelle avec des styles personnalisables. Parfait pour les vidéos de produits, les vidéos explicatives et les médias sociaux.",
      it: "Qualità costantemente elevata con strumenti IA moderni. Aspetto professionale con stili personalizzabili. Perfetto per video di prodotti, video esplicativi e social media."
    },
    qualityTraditional: {
      de: "Abhängig vom Budget und der Crew. Kann bei ausreichenden Ressourcen sehr hochwertig sein, aber auch stark variieren. Unvorhersehbare Faktoren können die Qualität beeinträchtigen.",
      en: "Dependent on budget and crew. Can be very high quality with sufficient resources, but can also vary greatly. Unpredictable factors can affect quality.",
      fr: "Dépend du budget et de l'équipe. Peut être de très haute qualité avec des ressources suffisantes, mais peut également varier considérablement. Des facteurs imprévisibles peuvent affecter la qualité.",
      it: "Dipende dal budget e dalla troupe. Può essere di altissima qualità con risorse sufficienti, ma può anche variare notevolmente. Fattori imprevedibili possono influenzare la qualità."
    },
    roiHeading: {
      de: "ROI (Return on Investment)",
      en: "ROI (Return on Investment)",
      fr: "ROI (Retour sur Investissement)",
      it: "ROI (Ritorno sull'Investimento)"
    },
    roiAI: {
      de: "Höherer ROI durch niedrigere Kosten und schnellere Produktion. Mehr Videos für dasselbe Budget. Schnellere Markteinführung neuer Kampagnen.",
      en: "Higher ROI through lower costs and faster production. More videos for the same budget. Faster market launch of new campaigns.",
      fr: "ROI plus élevé grâce à des coûts plus bas et une production plus rapide. Plus de vidéos pour le même budget. Lancement plus rapide de nouvelles campagnes.",
      it: "ROI più elevato grazie a costi inferiori e produzione più rapida. Più video per lo stesso budget. Lancio sul mercato più rapido di nuove campagne."
    },
    roiTraditional: {
      de: "Niedrigerer ROI aufgrund hoher Produktionskosten. Weniger Videos für dasselbe Budget. Längere Zeit bis zur Markteinführung.",
      en: "Lower ROI due to high production costs. Fewer videos for the same budget. Longer time to market.",
      fr: "ROI inférieur en raison des coûts de production élevés. Moins de vidéos pour le même budget. Délai de mise sur le marché plus long.",
      it: "ROI inferiore a causa degli alti costi di produzione. Meno video per lo stesso budget. Tempi più lunghi per arrivare sul mercato."
    },
    conclusion: {
      de: "KI-Videomarketing bietet einen klaren Vorteil in Bezug auf Kosten, Zeit und ROI, während traditionelle Produktion in bestimmten Szenarien weiterhin Stärken hat. Die meisten Schweizer Unternehmen profitieren von einem hybriden Ansatz, bei dem KI-Videos für regelmäßigen Content und gezielte traditionelle Produktion für besondere Projekte eingesetzt werden.",
      en: "AI video marketing offers a clear advantage in terms of cost, time, and ROI, while traditional production still has strengths in certain scenarios. Most Swiss businesses benefit from a hybrid approach, using AI videos for regular content and targeted traditional production for special projects.",
      fr: "Le marketing vidéo IA offre un avantage clair en termes de coût, de temps et de ROI, tandis que la production traditionnelle conserve des points forts dans certains scénarios. La plupart des entreprises suisses bénéficient d'une approche hybride, utilisant des vidéos IA pour le contenu régulier et une production traditionnelle ciblée pour des projets spéciaux.",
      it: "Il marketing video IA offre un chiaro vantaggio in termini di costo, tempo e ROI, mentre la produzione tradizionale mantiene punti di forza in determinati scenari. La maggior parte delle aziende svizzere trae vantaggio da un approccio ibrido, utilizzando video IA per contenuti regolari e produzione tradizionale mirata per progetti speciali."
    },
    callToAction: {
      de: "Bereit für KI-Videomarketing?",
      en: "Ready for AI video marketing?",
      fr: "Prêt pour le marketing vidéo IA?",
      it: "Pronto per il marketing video IA?"
    },
    learnMore: {
      de: "Mehr erfahren",
      en: "Learn more",
      fr: "En savoir plus",
      it: "Scopri di più"
    },
    productVideos: {
      de: "KI-Produktvideos",
      en: "AI Product Videos",
      fr: "Vidéos de produits IA",
      it: "Video di prodotti IA"
    },
    socialMediaAds: {
      de: "KI-Social-Media-Videos",
      en: "AI Social Media Videos",
      fr: "Vidéos médias sociaux IA",
      it: "Video social media IA"
    }
  };
  
  const t = (key: keyof typeof translations) => {
    return translations[key][currentLanguage as keyof typeof translations[typeof key]] || '';
  };
  
  return (
    <div className="py-12 px-4 md:py-16 max-w-7xl mx-auto">
      {/* Hero section */}
      <div className="text-center mb-16">
        <h1 className="text-3xl md:text-5xl font-bold text-white mb-4">{t('title')}</h1>
        <p className="text-xl text-purple-300 mb-8">{t('subtitle')}</p>
        <div className="max-w-3xl mx-auto">
          <p className="text-gray-300 leading-relaxed">{t('intro')}</p>
        </div>
      </div>
      
      {/* Comparison table */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
        <div className="col-span-1 md:col-span-3">
          <div className="bg-gray-900 rounded-lg overflow-hidden border border-purple-900/30">
            <div className="grid grid-cols-3">
              {/* Header row */}
              <div className="p-4 bg-gray-800">
                <span className="font-bold text-white">Comparison</span>
              </div>
              <div className="p-4 bg-purple-900/50">
                <span className="font-bold text-white">AI Video Marketing</span>
              </div>
              <div className="p-4 bg-gray-800">
                <span className="font-bold text-white">Traditional Production</span>
              </div>
              
              {/* Cost row */}
              <div className="p-4 bg-gray-900 flex items-center">
                <DollarSign size={20} className="text-purple-400 mr-2" />
                <span className="font-bold text-white">{t('costHeading')}</span>
              </div>
              <div className="p-4 bg-gray-800 border-l border-gray-700">
                <p className="text-green-400 mb-2">✓ {currentLanguage === 'de' ? 'Niedrige Kosten' : 
                                                  currentLanguage === 'en' ? 'Low Cost' : 
                                                  currentLanguage === 'fr' ? 'Coût bas' : 'Costo basso'}</p>
                <p className="text-gray-300">{t('costAI')}</p>
              </div>
              <div className="p-4 bg-gray-900 border-l border-gray-700">
                <p className="text-red-400 mb-2">✗ {currentLanguage === 'de' ? 'Hohe Kosten' : 
                                                 currentLanguage === 'en' ? 'High Cost' : 
                                                 currentLanguage === 'fr' ? 'Coût élevé' : 'Costo alto'}</p>
                <p className="text-gray-300">{t('costTraditional')}</p>
              </div>
              
              {/* Time row */}
              <div className="p-4 bg-gray-900 flex items-center">
                <Clock size={20} className="text-purple-400 mr-2" />
                <span className="font-bold text-white">{t('timeHeading')}</span>
              </div>
              <div className="p-4 bg-gray-800 border-l border-gray-700">
                <p className="text-green-400 mb-2">✓ {currentLanguage === 'de' ? 'Schnelle Produktion' : 
                                                  currentLanguage === 'en' ? 'Fast Production' : 
                                                  currentLanguage === 'fr' ? 'Production rapide' : 'Produzione rapida'}</p>
                <p className="text-gray-300">{t('timeAI')}</p>
              </div>
              <div className="p-4 bg-gray-900 border-l border-gray-700">
                <p className="text-red-400 mb-2">✗ {currentLanguage === 'de' ? 'Langsame Produktion' : 
                                                 currentLanguage === 'en' ? 'Slow Production' : 
                                                 currentLanguage === 'fr' ? 'Production lente' : 'Produzione lenta'}</p>
                <p className="text-gray-300">{t('timeTraditional')}</p>
              </div>
              
              {/* Quality row */}
              <div className="p-4 bg-gray-900 flex items-center">
                <CheckCircle size={20} className="text-purple-400 mr-2" />
                <span className="font-bold text-white">{t('qualityHeading')}</span>
              </div>
              <div className="p-4 bg-gray-800 border-l border-gray-700">
                <p className="text-green-400 mb-2">✓ {currentLanguage === 'de' ? 'Konsistente Qualität' : 
                                                  currentLanguage === 'en' ? 'Consistent Quality' : 
                                                  currentLanguage === 'fr' ? 'Qualité constante' : 'Qualità costante'}</p>
                <p className="text-gray-300">{t('qualityAI')}</p>
              </div>
              <div className="p-4 bg-gray-900 border-l border-gray-700">
                <p className="text-yellow-400 mb-2">⟳ {currentLanguage === 'de' ? 'Variable Qualität' : 
                                                   currentLanguage === 'en' ? 'Variable Quality' : 
                                                   currentLanguage === 'fr' ? 'Qualité variable' : 'Qualità variabile'}</p>
                <p className="text-gray-300">{t('qualityTraditional')}</p>
              </div>
              
              {/* ROI row */}
              <div className="p-4 bg-gray-900 flex items-center">
                <BarChart4 size={20} className="text-purple-400 mr-2" />
                <span className="font-bold text-white">{t('roiHeading')}</span>
              </div>
              <div className="p-4 bg-gray-800 border-l border-gray-700">
                <p className="text-green-400 mb-2">✓ {currentLanguage === 'de' ? 'Hoher ROI' : 
                                                  currentLanguage === 'en' ? 'High ROI' : 
                                                  currentLanguage === 'fr' ? 'ROI élevé' : 'ROI alto'}</p>
                <p className="text-gray-300">{t('roiAI')}</p>
              </div>
              <div className="p-4 bg-gray-900 border-l border-gray-700">
                <p className="text-red-400 mb-2">✗ {currentLanguage === 'de' ? 'Niedriger ROI' : 
                                                 currentLanguage === 'en' ? 'Low ROI' : 
                                                 currentLanguage === 'fr' ? 'ROI bas' : 'ROI basso'}</p>
                <p className="text-gray-300">{t('roiTraditional')}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Conclusion */}
      <div className="max-w-3xl mx-auto text-center mb-16">
        <h2 className="text-2xl font-bold text-white mb-4">
          {currentLanguage === 'de' ? 'Fazit' : 
          currentLanguage === 'en' ? 'Conclusion' : 
          currentLanguage === 'fr' ? 'Conclusion' : 'Conclusione'}
        </h2>
        <p className="text-gray-300 mb-8">{t('conclusion')}</p>
      </div>
      
      {/* CTA section */}
      <div className="bg-gradient-to-r from-purple-900/30 to-indigo-900/30 rounded-2xl p-8 md:p-12 text-center">
        <h2 className="text-2xl md:text-3xl font-bold text-white mb-6">{t('callToAction')}</h2>
        <div className="flex flex-col md:flex-row gap-4 justify-center">
          <Link to={getServicePath('product-videos')}>
            <Button variant="secondary" size="lg" className="flex items-center gap-2">
              {t('productVideos')} <ArrowRight size={16} />
            </Button>
          </Link>
          <Link to={getServicePath('social-media-ads')}>
            <Button variant="secondary" size="lg" className="flex items-center gap-2">
              {t('socialMediaAds')} <ArrowRight size={16} />
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ComparisonContent;
